import React, { useState, useMemo, useRef } from "react";
import type { Key } from "react";
import { Button, Col, Divider, Form, Menu, Modal, Row } from "antd";
import { useAppDispatch, useAppSelector } from "../../..";
import { AgreementsSearchResult } from "../AgreementSearchResult/AgreementsSearchResult";
import { SearchFiltersOrganization } from "../SearchFiltersOrganization/SearchFiltersOrganization";
import { SearchFiltersIndividual } from "../SearchFiltersIndividual/SearchFiltersIndividual";
import type { IClientType } from "../../../lib/types";
import { checkIsLoading } from "../../../lib/utils";
import { IAgreementSearchParams } from "../../../store/slices/agreementsSearch/types/IAgreementSearchParams";
import { hasAccess } from "../../RolesModule/hasAccess";
import {
  MENU_ACCOUNTS,
  MENU_ACCOUNTS_INDIVIDUAL,
  MENU_ACCOUNTS_INDIVIDUAL_WRITE,
  MENU_ACCOUNTS_ORGANIZATION,
  MENU_ACCOUNTS_ORGANIZATION_WRITE,
  MENU_ACCOUNTS_WRITE,
} from "../../../lib/const/permissions";
import {
  fetchAgreements,
  agreementsSearchActions,
} from "../../../store/slices/agreementsSearch/agreementsSearch";
import "./AgreementsSearchModal.less";

export interface AgreementsSearchTableData {
  key: Key;
  accountId: number;
  fullName: string;
  agreementNumber: string[];
  id: number;
  externalId: string[];
  inn?: string | number;
  bin?: string | number;
  kpp?: string | number;
  phones?: string[];
  selected?: boolean;
  fromDate: string;
}

interface ComponentProps {
  visible: boolean;
  okText?: string;
  onClose: () => void;
  toggleClientType: () => void;
  onSubmit: (
    accountIds: number[],
    agreement?: {
      agreementId: number;
      agreementNumber: string;
      fullName: string;
      accountId: number;
      inn: string | number | undefined;
      fromDate: string;
    }[]
  ) => void;
  clientType: IClientType;
  multiselect?: boolean;
  combineAgreements?: boolean;
}

export const AgreementsSearchModal = (props: ComponentProps) => {
  const {
    visible,
    okText = "Выбрать клиента",
    onClose,
    onSubmit,
    clientType,
    toggleClientType,
    multiselect = false,
    combineAgreements = false,
  } = props;
  const dispatch = useAppDispatch();

  const { agreementsLoading } = useAppSelector((state) => state.agreementsSearch);

  const [form] = Form.useForm<IAgreementSearchParams>();
  const [selectedAgreements, setSelectedAgreements] = useState<AgreementsSearchTableData[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(0);

  const hasAccessToOrganization: boolean = useMemo(
    () =>
      hasAccess(
        MENU_ACCOUNTS_ORGANIZATION,
        MENU_ACCOUNTS_ORGANIZATION_WRITE,
        MENU_ACCOUNTS,
        MENU_ACCOUNTS_WRITE
      ),
    [hasAccess]
  );

  const hasAccessToIndividual: boolean = useMemo(
    () => hasAccess(MENU_ACCOUNTS_INDIVIDUAL, MENU_ACCOUNTS_INDIVIDUAL_WRITE),
    [hasAccess]
  );

  const { tenantPropertiesDto } = useAppSelector((state) => state.tenants);
  const displayedPropertyNames: string[] = useMemo(
    () =>
      tenantPropertiesDto
        .filter(({ propertyType: { alias } }) => alias === "client.company_attributes")
        .map(({ stringValue }) => (stringValue as string).toLowerCase()),
    [tenantPropertiesDto]
  );

  const onResetSearchFilters = () => {
    setCurrentPage(0);
    form.resetFields();
    setSelectedAgreements([]);
    dispatch(agreementsSearchActions.cleanAgreements());
    dispatch(agreementsSearchActions.cleanAgreementsData());
  };

  const onResetSearchFiltersForApply = () => {
    setCurrentPage(0);
    dispatch(agreementsSearchActions.cleanAgreements());
    dispatch(agreementsSearchActions.cleanAgreementsData());

    const table = tableRef.current?.querySelector(".ant-table-body");
    table && table.scrollTo({ top: 0 });
  };

  const onSubmitChangeClientType = () => {
    onResetSearchFilters();
    toggleClientType();
  };

  const onChangeClientType = (type: IClientType) => {
    if (clientType === type) return;
    if (selectedAgreements.length > 0) {
      Modal.confirm({
        content: "При переключении на новую вкладку список выбранных клиентов будет очищен",
        onOk: onSubmitChangeClientType,
        cancelText: "Отмена",
        okText: "Выполнить переход",
      });
    } else {
      onSubmitChangeClientType();
    }
  };

  const onModalClose = () => {
    onClose();
    onResetSearchFilters();
  };

  const onModalSubmit = () => {
    if (selectedAgreements) {
      const accountIds = selectedAgreements.map(({ accountId }) => accountId);
      const agreements = selectedAgreements.map(
        ({ id, agreementNumber, fullName, accountId, inn, fromDate }) => ({
          agreementId: id,
          agreementNumber: agreementNumber[0],
          fullName,
          accountId,
          inn,
          fromDate,
        })
      );
      onSubmit(accountIds, agreements);
      onResetSearchFilters();
    }
  };

  const onSearchOrganization = (values: IAgreementSearchParams) => {
    // return console.log(values);
    const searchParams: IAgreementSearchParams = {
      "organization.fullName": values["organization.fullName"],
      "subscriber.externalId": values["subscriber.externalId"],
      agreementNumber: values.agreementNumber,
      agreementIds: values.agreementIds,
      accountIds: values.accountIds,
      page: currentPage,
    };
    // Метод поиска предполагает передачу только одного свойства, поэтому если есть и БИН и ИНН, передастся только ИНН
    if (displayedPropertyNames.includes("kpp") && values.kpp) {
      searchParams["organization.property.typeId"] = "KPP";
      searchParams["organization.property.stringValue"] = values.kpp;
    }
    if (displayedPropertyNames.includes("inn") && values.inn) {
      searchParams["organization.property.typeId"] = "INN";
      searchParams["organization.property.stringValue"] = values.inn;
    }
    if (displayedPropertyNames.includes("bin") && values.bin) {
      searchParams["organization.property.typeId"] = "BIN";
      searchParams["organization.property.stringValue"] = values.bin;
    }
    return dispatch(fetchAgreements(searchParams));
  };

  const onSearchIndividual = (values: IAgreementSearchParams) => {
    // return console.log(values);
    const searchParams: IAgreementSearchParams = {
      "individual.fullName": values["individual.fullName"],
      "subscriber.externalId": values["subscriber.externalId"],
      "individual.phone": values["individual.phone"],
      agreementNumber: values.agreementNumber,
      agreementIds: values.agreementIds,
      accountIds: values.accountIds,
      page: currentPage,
    };
    return dispatch(fetchAgreements(searchParams));
  };

  const onSearch = (values: IAgreementSearchParams) => {
    const hasSomeValue = Object.values(values).filter(Boolean).length > 0;
    if (hasSomeValue) {
      switch (clientType) {
        case "ORGANIZATION":
          return onSearchOrganization(values);
        case "INDIVIDUAL":
          return onSearchIndividual(values);
      }
    }
  };

  const tableRef = useRef<HTMLDivElement>(null);

  return (
    <Modal
      title="Поиск клиента"
      visible={visible}
      okText={okText}
      onCancel={onModalClose}
      onOk={onModalSubmit}
      width="941px"
      className="agreements-search-modal"
      bodyStyle={{
        paddingTop: 0,
      }}
      okButtonProps={{
        style: {
          marginLeft: "10px",
        },
        disabled: selectedAgreements.length === 0,
      }}
    >
      <Menu selectedKeys={[clientType]} mode="horizontal">
        {hasAccessToOrganization && (
          <Menu.Item
            key="ORGANIZATION"
            onClick={() => {
              onChangeClientType("ORGANIZATION");
            }}
          >
            Юридическое лицо
          </Menu.Item>
        )}
        {hasAccessToIndividual && (
          <Menu.Item
            key="INDIVIDUAL"
            onClick={() => {
              onChangeClientType("INDIVIDUAL");
            }}
          >
            Физическое лицо
          </Menu.Item>
        )}
      </Menu>
      <Divider style={{ margin: "13px 0 10px" }} />
      <Form
        form={form}
        labelCol={{ span: 9 }}
        wrapperCol={{ span: 15 }}
        labelAlign="left"
        onFinish={onSearch}
      >
        {clientType === "ORGANIZATION" && (
          <SearchFiltersOrganization displayedPropertyNames={displayedPropertyNames} />
        )}
        {clientType === "INDIVIDUAL" && <SearchFiltersIndividual />}
        <Row gutter={10} justify="end">
          <Col>
            <Button onClick={onResetSearchFilters}>Сброс параметров</Button>
          </Col>
          <Col>
            <Button
              type="primary"
              htmlType="submit"
              loading={checkIsLoading(agreementsLoading)}
              onClick={onResetSearchFiltersForApply}
            >
              Найти
            </Button>
          </Col>
        </Row>
      </Form>
      {/* Для корректного поведения столбцов по hidden, разнесены для разных clientType */}
      {clientType === "ORGANIZATION" && (
        <AgreementsSearchResult
          displayedPropertyNames={displayedPropertyNames}
          clientType="ORGANIZATION"
          selectedAgreements={selectedAgreements}
          setSelectedAgreements={setSelectedAgreements}
          combineAgreements={combineAgreements}
          multiselect={multiselect}
          form={form}
          tableRef={tableRef}
          setCurrentPage={setCurrentPage}
        />
      )}
      {clientType === "INDIVIDUAL" && (
        <AgreementsSearchResult
          displayedPropertyNames={displayedPropertyNames}
          clientType="INDIVIDUAL"
          selectedAgreements={selectedAgreements}
          setSelectedAgreements={setSelectedAgreements}
          combineAgreements={combineAgreements}
          multiselect={multiselect}
          form={form}
          tableRef={tableRef}
          setCurrentPage={setCurrentPage}
        />
      )}
    </Modal>
  );
};
